import { useEffect, useRef, useState } from "react";
import Button from "@/common/elements/Button";
import DataTable from "@/common/elements/DataTable";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
  StyledSubTableContainer,
} from "@/common/styles";
import CustomModal from "@/components/CustomModal";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import TextField from "@/common/elements/InputElements/TextField";
import { Form } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { setBookings } from "@/slices/bookingsSlice";
import CashAccountPaymentForm from "@/components/Warrants/CashAccountPaymentForm";
import CashAccountDetailForm from "@/components/Warrants/CashAccountDetailForm";
import dayjs from "dayjs";
import {
  isNonEmptyVariable,
  getRefListNameByID,
  convertApiFields,
  getSQLTypeToHTMLType,
  debounce,
} from "@/util/common_function";
import DynamicForm from "@/common/DynamicForm";
import { BookingsSectionTitle } from "@/components/BookingsDetails/styles";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";

// CashAccountingNewExtra's own PK/FK columns, lower-first — mirrors
// AdditionalInmateMedicalInfo.jsx's INMATE_MEDICAL_EXTRA_ID_KEY constants.
// The ASP source's DBSchema.KeyField says "CashAccountingExtraID" (no
// "New"), while fieldsData.js lists "CashAccountingNewExtraID" (matching
// the table name) — leaning on the ASP source, same as the
// ArrestReportsExtra/InmatePropertyItemsExtra precedent, but unverified.
const CASH_ACCOUNTING_NEW_EXTRA_ID_KEY = "cashAccountingExtraID";
const CASH_ACCOUNTING_ID_COLUMN = "cashAccountingID";

const CashAccountDetails = ({startingPoint = ""}) => {
  const dispatch = useDispatch();

  const [detailsModal, setDetailsModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [feeOrFineModal, setFeeOrFineModal] = useState(false);
  const [cashDetails, setCashDetails] = useState();
  const [selectedIndex, setSelectedIndex] = useState();
  const [selectedData, setSelectedData] = useState();
  const formatSerial = (index) => String(index + 1).padStart(3, "0");
  const { userDetail } = useSelector((s) => s?.userDetail);

  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings
  );

  const getTransactions = () => bookings?.bookingCashAccount?.[0]?.cashAccountingNew?.[0]?.cashAccountingNewTransactions || [];

  // -----------------------------------------------------------------------
  // CashAccountingNewExtra — PCI's per-client dynamic "_U" columns for this
  // cash account (CashAccountingInterfaceCommon.asp's "Additional
  // Information" section, shown right after Comments). cashAccountingNew is
  // a normal onetoone entity on the booking (see bookingTree.js), so its
  // base row (and cashAccountingId) is expected to come back nested under
  // bookings.bookingCashAccount[0].cashAccountingNew[0] from the ordinary
  // booking GET/save cycle — no separate resolve call for the parent id,
  // same reasoning as AdditionalInmateMedicalInfo.jsx.
  // -----------------------------------------------------------------------
  const [cashAccountExtraForm] = Form.useForm();
  const [cashAccountExtraFields, setCashAccountExtraFields] = useState([]);
  const [cashAccountExtraFieldsReady, setCashAccountExtraFieldsReady] =
    useState(false);
  const cashAccountExtraTypeMapRef = useRef({});
  const [cashAccountExtraFormValues, setCashAccountExtraFormValues] =
    useState(null);
  const bookingsRef = useRef(bookings);
  useEffect(() => {
    bookingsRef.current = bookings;
  }, [bookings]);

  useEffect(() => {
    api
      .get(`${API_ENDPOINTS.getTableColumns}/CashAccountingNewExtra`)
      .then((response) => {
        const tableData = (response?.data || []).filter((d) =>
          d?.columnName?.endsWith("_U")
        );
        const converted = convertApiFields(tableData, "CashAccountingNewExtra");
        const typeMap = {};
        converted.forEach((c) => {
          typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type);
        });
        cashAccountExtraTypeMapRef.current = typeMap;
        setCashAccountExtraFields(converted);
        setCashAccountExtraFieldsReady(true);
      })
      .catch((error) => {
        console.error(
          "[CashAccountDetails] CashAccountingNewExtra field defs fetch failed:",
          error
        );
      });
  }, []);

  useEffect(() => {
    if (!cashAccountExtraFieldsReady) return;

    const info =
      bookings?.bookingCashAccount?.[0]?.cashAccountingNew?.[0]
        ?.cashAccountingNewExtra?.[0];
    const withDates = info
      ? Object.keys(info).reduce((acc, key) => {
          const val = info[key];
          const htmlType = cashAccountExtraTypeMapRef.current[key];
          const isDateField = htmlType === "date" || htmlType === "datetime";
          acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
          return acc;
        }, {})
      : info;
    setCashAccountExtraFormValues(withDates);
  }, [bookings, cashAccountExtraFieldsReady]);

  useEffect(() => {
    if (isNonEmptyVariable(cashAccountExtraFormValues)) {
      cashAccountExtraForm.setFieldsValue(cashAccountExtraFormValues);
    } else {
      cashAccountExtraForm.resetFields();
    }
  }, [cashAccountExtraFormValues]);

  const updateCashAccountExtraForm = (currentValues) => {
    const targetAccount = bookingsRef.current?.bookingCashAccount?.[0] || {};
    const cashAccountingNew = targetAccount?.cashAccountingNew?.[0] || {};
    const existingExtra = cashAccountingNew?.cashAccountingNewExtra?.[0] || {};

    const formattedValues = Object.keys(currentValues).reduce((acc, key) => {
      const val = currentValues[key];
      const htmlType = cashAccountExtraTypeMapRef.current[key];
      if (val) {
        acc[key] =
          htmlType === "date"
            ? dayjs(val).format("MM/DD/YYYY")
            : htmlType === "datetime"
              ? dayjs(val).format("MM/DD/YYYY HH:mm")
              : val;
      } else {
        acc[key] = null;
      }
      return acc;
    }, {});

    dispatch(
      setBookings({
        bookingCashAccount: [
          {
            ...targetAccount,
            cashAccountingNew: [
              {
                ...cashAccountingNew,
                cashAccountingNewExtra: [
                  {
                    ...existingExtra,
                    ...formattedValues,
                    [CASH_ACCOUNTING_NEW_EXTRA_ID_KEY]:
                      existingExtra?.[CASH_ACCOUNTING_NEW_EXTRA_ID_KEY] ?? 0,
                    [CASH_ACCOUNTING_ID_COLUMN]:
                      existingExtra?.[CASH_ACCOUNTING_ID_COLUMN] ??
                      cashAccountingNew?.cashAccountingId ??
                      0,
                  },
                ],
              },
            ],
          },
        ],
      })
    );
  };

  // Debounced so rapid keystrokes/onFieldsChange events don't dispatch on
  // every single one — matches AdditionalInmateMedicalInfo.jsx's
  // dataModifyRef.
  const cashAccountExtraDataModifyRef = useRef(null);
  if (!cashAccountExtraDataModifyRef.current) {
    cashAccountExtraDataModifyRef.current = debounce(() => {
      updateCashAccountExtraForm(cashAccountExtraForm.getFieldsValue());
    }, 800);
  }

  const tableHeaders = [
    { key: "id", text: "#" },
    { key: "dateTime", text: "Date Time" },
    {
      key: "user",
      text: "User",
      render: (text) => {
        const staffID = !isNaN(text) ? parseInt(text) : null;
        if (isNonEmptyVariable(staffID))
          return `${getRefListNameByID("", staffID, true, true)}`;
        else return `${text}`;
      },
    },
    { key: "category", text: "Category" },
    { key: "amount", text: "Amount" },
    { key: "transactionDetails", text: "Transaction Details" },
    { key: "receiptNumber", text: "Receipt Number" },
    { key: "transactionType", text: "Transaction Type" },
  ];

  const inmateCashAccountRef = {
    cashDeposit: useRef(),
    purchase: useRef(),
  };

  useEffect(() => {
    const data =
      bookings.bookingCashAccount?.[0]?.cashAccountingNewTransactions?.map(
        (cashAccountData, index) => ({
          id: index,
          dateTime: null,
          user: cashAccountData?.user,
          category: cashAccountData?.category,
          amount: cashAccountData?.amount,
          transactionDetails: cashAccountData?.transactionDetails,
          receiptNumber: cashAccountData?.receiptNumber,
          transactionType: cashAccountData?.transactionType,
        })
      );
    setCashDetails(data);
  }, []);

  const handleDeposit = () => {
    setPaymentModal(true);
  };

  const handlePurchase = () => {
    setFeeOrFineModal(true);
  };

  const handleDetailsModal = () => {
    if (selectedData) {
      setDetailsModal(true);
    }
  };

  const handleDelete = () => {
    if (!isNonEmptyVariable(selectedData)) return;

    const targetAccount = structuredClone(bookings?.bookingCashAccount?.[0] || {});

    const transactions = [
      ...(targetAccount?.cashAccountingNew?.[0]?.cashAccountingNewTransactions || []),
    ];

    const updatedTransactions = transactions.filter((tx, index) => {
      // ← match by receiptNumber if available, fallback to index
      if (isNonEmptyVariable(selectedData?.receiptNumber)) {
        return tx.receiptNumber !== selectedData?.receiptNumber;
      }
      return formatSerial(index) !== selectedData?.id;
    });

    if (!targetAccount.cashAccountingNew?.[0]) {
      targetAccount.cashAccountingNew = [{ cashAccountingNewTransactions: [] }];
    }

    targetAccount.cashAccountingNew[0].cashAccountingNewTransactions = updatedTransactions;

    dispatch(setBookings({ bookingCashAccount: [targetAccount] }));

    setSelectedData(null);
    setSelectedIndex(null);
  };

  const handleOnSave = async () => {
    let values;
    let usedRef;
    try {
      if (paymentModal) {
        await inmateCashAccountRef.cashDeposit.current.validateFields();
        values = inmateCashAccountRef.cashDeposit.current.getFormValues();
        values.transactionType = "Credit";
        usedRef = inmateCashAccountRef.cashDeposit.current;
      } else {
        await inmateCashAccountRef.purchase.current.validateFields();
        values = inmateCashAccountRef.purchase.current.getFormValues();
        values.transactionType = "Debit";
        usedRef = inmateCashAccountRef.purchase.current;
      }
    } catch (err) {
      return;
    }

    // Split CashAccountingNewTransactionsExtra dynamic fields out of the
    // form values — nested onto cashAccountingNewTransactionsExtra[0] below
    // rather than left flat on values, where they'd otherwise leak into the
    // main bookings save payload. See pages/Bookings/index.jsx's
    // strip-before-POST and saveCashAccountingNewTransactionsExtra.
    const transactionExtraFieldKeys = usedRef?.transactionExtraFieldKeys || [];
    const transactionExtraTypeMap = usedRef?.transactionExtraTypeMap || {};
    const transactionExtraData = {};
    transactionExtraFieldKeys.forEach((k) => {
      const val = values?.[k];
      const htmlType = transactionExtraTypeMap[k];
      transactionExtraData[k] = val
        ? htmlType === "date"
          ? dayjs(val).format("MM/DD/YYYY")
          : htmlType === "datetime"
            ? dayjs(val).format("MM/DD/YYYY HH:mm")
            : val
        : null;
      delete values[k];
    });

    const targetAccount = structuredClone(bookings?.bookingCashAccount?.[0] || {});

    const transactions = [
      ...(targetAccount?.cashAccountingNew?.[0]?.cashAccountingNewTransactions || []),
    ];

    if (detailsModal) {
      const transactionIndex = transactions.findIndex(
        (tx) => tx.receiptNumber === selectedData?.receiptNumber
      );

      if (transactionIndex !== -1) {
        const existingExtra =
          transactions[transactionIndex]?.cashAccountingNewTransactionsExtra?.[0] ||
          {};
        transactions[transactionIndex] = {
          ...transactions[transactionIndex],
          ...values,
          cashAccountingNewTransactionsExtra:
            transactionExtraFieldKeys.length > 0
              ? [
                  {
                    ...existingExtra,
                    ...transactionExtraData,
                    cashAccountingTransactionExtraID:
                      existingExtra?.cashAccountingTransactionExtraID ??
                      usedRef?.transactionExtraID ??
                      0,
                    cashAccountingTransactionID:
                      existingExtra?.cashAccountingTransactionID ??
                      selectedData?.cashAccountingTransactionId ??
                      0,
                  },
                ]
              : transactions[transactionIndex]?.cashAccountingNewTransactionsExtra,
        };
      }
    } else {
      console.log("userDetail : ", userDetail);
      const newTransaction = {
        ...values,
        cashAccountingTransactionId: 0,
        amount: parseFloat(values?.amount) || 0,
        user: userDetail?.username || null,
        receivedBy: values?.receivedBy ? getRefListNameByID("", values?.receivedBy, true) : null,
        receivedByStaffId: values?.receivedBy || null,
        creatorStaffId: userDetail?.staffId || null,
        dateAdded: dayjs().format("MM/DD/YYYY"),
        timeAdded: dayjs().format("HH:mm"),
        // dateTime: dayjs().format("MM/DD/YYYY HH:mm"),
        cashAccountingNewTransactionsExtra:
          transactionExtraFieldKeys.length > 0
            ? [
                {
                  ...transactionExtraData,
                  cashAccountingTransactionExtraID: 0,
                  cashAccountingTransactionID: 0,
                },
              ]
            : undefined,
      };
      transactions.push(newTransaction);
    }

    if (!targetAccount.cashAccountingNew?.[0]) {
      targetAccount.cashAccountingNew = [{ cashAccountingNewTransactions: [] }];
    }
    targetAccount.cashAccountingNew[0].cashAccountingNewTransactions = transactions;
    dispatch(setBookings({ bookingCashAccount: [targetAccount] }));

    setPaymentModal(false);
    setDetailsModal(false);
    setFeeOrFineModal(false);
    setSelectedData(null);   // ← clear selection after save
  };

  const handleRowClick = (value, index) => {
    if (isNonEmptyVariable(value)) {
      setSelectedData(value);
      setSelectedIndex(index);
    }

    // Double click passes index — open details modal directly
    if (index !== undefined && index !== null) {
      setDetailsModal(true);
    }
  };

  const balance = getTransactions().reduce((acc, tx) => {
    const amt = parseFloat(tx?.amount?.parsedValue ?? tx?.amount) || 0;
    return tx?.transactionType === "Credit" ? acc + amt : acc - amt;
  }, 0);

  return (
    <>
      <StyledSectionHeading marginBottom="0px" marginTop="20px">
        <span>Cash Account</span>
      </StyledSectionHeading>

      <StyledSubTableContainer>
        <StyledFlex justify="space-between" align="center" marginBottom="5px">
          <h2 style={{ margin: "0" }}>Account Transactions</h2>
          <StyledFlex gap="10px">
            <Button onClick={handleDeposit} disabled={!bookingsFieldsEnabled}>Deposit</Button>
            <Button onClick={handlePurchase} disabled={!bookingsFieldsEnabled}>Purchase</Button>
            <Button
              onClick={handleDetailsModal}
              disabled={!isNonEmptyVariable(selectedData)}
            >
              Details
            </Button>
            <Button onClick={handleDelete} disabled={!selectedData || !bookingsFieldsEnabled}>
              Delete
            </Button>
          </StyledFlex>
        </StyledFlex>

        <DataTable
          headers={tableHeaders}
          data={getTransactions().map((tx, index) => ({
            ...tx,
            id: formatSerial(index),
            dateTime: tx?.dateAdded && tx?.timeAdded
              ? `${tx.dateAdded} ${tx.timeAdded}`
              : tx?.dateAdded || null,
            amount: tx?.amount?.parsedValue ?? tx?.amount,
          }))}
          onRowClick={handleRowClick}
          rowUniqueId="cashAccountingTransactionId"
          totalRecords={getTransactions().length}
          selectionColumnVisible={false}
          className="cash-account-table"
        />
        <div style={{ textAlign: "right" }}>
          <span>
            <b>Balance:</b> ${balance.toFixed(2)}
          </span>
        </div>
      </StyledSubTableContainer>

      <StyledGridRow gutter={16}>
        <StyledGridCol lg={24} marginTop="15px">
          <FormItem label="Comments">
            <TextField inputType="textarea" rows="2" />
          </FormItem>
        </StyledGridCol>
      </StyledGridRow>

      {cashAccountExtraFields.length > 0 && (
        <>
          <BookingsSectionTitle>
            <span>Additional Information</span>
          </BookingsSectionTitle>
          <Form
            form={cashAccountExtraForm}
            onFieldsChange={() => {
              cashAccountExtraDataModifyRef.current();
            }}
            disabled={!bookingsFieldsEnabled}
          >
            <StyledGridRow gutter={16}>
              {cashAccountExtraFields.map((sf, index) => (
                <DynamicForm
                  key={index}
                  formAttributes={sf}
                  form={cashAccountExtraForm}
                  bookingsFieldsEnabled={bookingsFieldsEnabled}
                />
              ))}
            </StyledGridRow>
          </Form>
        </>
      )}

      {detailsModal && (
        <CustomModal
          show={detailsModal}
          onHide={() => setDetailsModal(false)}
          onSaveClick={handleOnSave}   // ← add this so save works here too
          header="Cash Account Transaction"
          size="lg"
          hideFooter={true}
        >
          <CashAccountDetailForm
            purchaseFormRef={inmateCashAccountRef.purchase}
            startingPoint={startingPoint}
            initialData={selectedData}
            viewOnly={true}
          />
        </CustomModal>
      )}

      {paymentModal && (
        <CustomModal
          show={paymentModal}
          onHide={() => setPaymentModal(false)}
          onSaveClick={handleOnSave}
          header="Enter New Payment For This Account"
          size="lg"
        >
          <CashAccountPaymentForm
            depositFormRef={inmateCashAccountRef.cashDeposit}
            startingPoint={startingPoint}
          />
        </CustomModal>
      )}

      {feeOrFineModal && (
        <CustomModal
          show={feeOrFineModal}
          onHide={() => setFeeOrFineModal(false)}
          onSaveClick={handleOnSave}
          header="Enter New Fee/Fine For This Account"
          size="lg"
        >
          <CashAccountDetailForm
            purchaseFormRef={inmateCashAccountRef.purchase}
            startingPoint={startingPoint}
          />
        </CustomModal>
      )}
    </>
  );
};
export default CashAccountDetails;
